import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query'
import { ApiError, del, get, getConfig, patch, post, put, upload } from './client'
import type { ApiResponse } from './generated'
import type {
  CheckDescriptor, Farmer, FarmerOrganisation, IngestDetail, Issue, Plot, PlotComment, PlotDetail, Preset, Preview,
  PlotRow, Producer, ProducerTotals, Region, RegionDetail, RegionField,
  RegionSummary, Run, RunConfig, Supplier, Benchmark, BasemapStyle, Country,
  AuthProviders, Principal, RunStatus,
} from './types'

export const keys = {
  me: ['me'] as const,
  authProviders: ['auth-providers'] as const,
  checks: ['checks'] as const,
  layers: ['reference-layers'] as const,
  basemap: ['basemap'] as const,
  suppliers: ['suppliers'] as const,
  countries: ['countries'] as const,
  presets: ['check-presets'] as const,
  ingest: (id: string) => ['ingest', id] as const,
  run: (id: string) => ['run', id] as const,
  issues: (id: string, q: string) => ['issues', id, q] as const,
  plots: (id: string, q: string) => ['plots', id, q] as const,
  plotRows: (id: string, q: string) => ['plot-rows', id, q] as const,
  plot: (id: string, runId?: string) => ['plot', id, runId ?? ''] as const,
  regions: ['regions'] as const,
  region: (id: string) => ['region', id] as const,
  regionFields: (id: string, q: string) => ['region-fields', id, q] as const,
  regionProducers: (id: string, q: string) => ['region-producers', id, q] as const,
}

/**
 * Who is holding this browser: a person, or a partner's root key.
 *
 * One call, because the application cannot decide what to render until it
 * knows which — and a root key reaches almost nothing, so rendering the
 * ordinary shell for one would be a screen full of 403s.
 */
export function usePrincipal() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => get<Principal>('/auth/me'),
    retry: false,
    staleTime: 5 * 60_000,
  })
}

export function useSession() {
  const { data, ...rest } = usePrincipal()
  return { ...rest, data: data?.user }
}

/** What this deployment accepts. The sign-in screen renders what it is told. */
/** What the API is running (GET /version): open, so it answers before sign-in. */
export const useApiVersion = () =>
  useQuery({
    queryKey: ['api-version'],
    queryFn: () => get<ApiResponse<'/version'>>('/version'),
    staleTime: 60_000,
    retry: false,
  })

export const useAuthProviders = () =>
  useQuery({
    queryKey: keys.authProviders,
    queryFn: () => get<AuthProviders>('/auth/providers'),
    staleTime: Infinity,
    retry: false,
  })

/**
 * What this session may reach.
 *
 * Two questions, not one: whether the person holds the tier, and whether
 * the credential they used is accepted for it. A platform admin signed in
 * with a password holds the tier and cannot use it, and showing them the
 * section would only produce a 403 they did not ask for.
 */
export function useCapabilities() {
  const { data: principal } = usePrincipal()
  const { data: providers } = useAuthProviders()
  const me = principal?.user
  const key = principal?.key
  const platformCredential =
    !providers?.platform_requires_auth0 || me?.auth_method === 'auth0'
  return {
    /**
     * A partner's root key. It manages that organisation's users and reads
     * its usage, and reaches nothing else — so the shell it gets is those
     * two screens and no more, rather than the ordinary one full of 403s.
     */
    key: Boolean(key && key.scope === 'users:manage'),
    /** Any key at all. No key holds regions or plots, whatever its scope. */
    anyKey: Boolean(key),
    org: Boolean(me?.is_org_admin || me?.is_platform_admin),
    /**
     * A platform key counts here.
     *
     * `requirePlatformAdmin` already accepts one — a machine has no browser
     * and cannot sign in through Auth0 — so refusing to render for it left
     * a valid credential looking at a rail of links that all answer 403.
     * The Auth0 rule constrains *sessions*, not this.
     */
    platform:
      (Boolean(me?.is_platform_admin) && platformCredential)
      || Boolean(key && key.scope !== 'users:manage'),
    /** They are one, but not through a credential the tier accepts. */
    platformNeedsAuth0: Boolean(me?.is_platform_admin) && !platformCredential,
    /** Whose login this is: the organisation's, a supplier's or a farmer's. */
    kind: (me?.kind ?? 'staff') as 'staff' | 'supplier' | 'farmer',
    /** What the organisation uses. Both unless the platform turned one off. */
    features: { eudr: me?.features?.eudr ?? true, sourcing: me?.features?.sourcing ?? true },
  }
}

export function useCheckCatalogue() {
  return useQuery({
    queryKey: keys.checks,
    queryFn: () =>
      get<{ checks: CheckDescriptor[]; required_fields: string[]; default_config: RunConfig }>('/checks'),
    staleTime: Infinity,
  })
}

export const useReferenceLayers = () =>
  useQuery({
    queryKey: keys.layers,
    queryFn: () => get<ApiResponse<'/reference-layers'>>('/reference-layers').then((r) => r.layers),
    staleTime: 5 * 60_000,
  })

export const useSuppliers = () =>
  useQuery({
    queryKey: keys.suppliers,
    queryFn: () => get<{ suppliers: Supplier[] }>('/suppliers').then((r) => r.suppliers),
  })

/**
 * The directory: suppliers, farmer organisations and farmers as records.
 * Any change to one can rename text on many plots, so plot queries are
 * refreshed along with the directory's own.
 */
function useAfterDirectoryChange() {
  const qc = useQueryClient()
  return () => {
    for (const key of ['suppliers', 'farmer-organisations', 'farmers', 'plot', 'plot-rows', 'plots', 'region']) {
      qc.invalidateQueries({ queryKey: [key] })
    }
  }
}

const searchParams = (params: Record<string, string | number | undefined>) =>
  new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]),
  ).toString()

export const useFarmerOrganisations = (params: { q?: string; supplier_id?: string; limit?: number } = {}) =>
  useQuery({
    queryKey: ['farmer-organisations', params],
    queryFn: () => get<{ farmer_organisations: FarmerOrganisation[] }>(`/farmer-organisations?${searchParams(params)}`)
      .then((r) => r.farmer_organisations),
    placeholderData: (prev) => prev,
  })

export const useFarmers = (params: { q?: string; supplier_id?: string; fo_id?: string; limit?: number; offset?: number } = {}) =>
  useQuery({
    queryKey: ['farmers', params],
    queryFn: () => get<{ farmers: Farmer[] }>(`/farmers?${searchParams(params)}`).then((r) => r.farmers),
    placeholderData: (prev) => prev,
  })

/** Plots the caller may open, across regions (GET /plots): a supplier's or farmer's own. */
export interface MyPlot {
  id: string
  plot_ref: string | null
  status: Plot['status']
  reviewed_at: string | null
  area_ha: number | null
  farmer_id: string | null
  fo: string | null
  region_id: string
  region_name: string
  region_open: boolean
  issue_count: number
}

export const useMyPlots = (params: { supplier_id?: string; farmer_id?: string; status?: string; q?: string; limit?: number; offset?: number } = {}) =>
  useQuery({
    queryKey: ['plots', 'mine', params],
    queryFn: () => get<{ plots: MyPlot[]; total: number }>(`/plots?${searchParams(params)}`),
    placeholderData: (prev) => prev,
  })

export const useMyPlotsGeoJson = (params: { supplier_id?: string; farmer_id?: string; status?: string } = {}) =>
  useQuery({
    queryKey: ['plots', 'mine-geojson', params],
    queryFn: () => get<GeoJSON.FeatureCollection>(`/plots.geojson?${searchParams({ ...params, simplify: '0.00001' })}`),
    retry: (count, err) => !(err instanceof ApiError && err.status === 422) && count < 1,
  })

export function useDirectoryMutations() {
  const after = useAfterDirectoryChange()
  const m = <A, R>(fn: (a: A) => Promise<R>) => useMutation({ mutationFn: fn, onSuccess: after })
  return {
    createSupplier: m((b: { name: string; country_code: string }) => post<Supplier>('/suppliers', b)),
    renameSupplier: m(({ id, name }: { id: string; name: string }) => patch<{ id: string }>(`/suppliers/${id}`, { name })),
    createFo: m((b: { name: string; code?: string | null; country_code?: string | null; supplier_id?: string }) =>
      post<FarmerOrganisation>('/farmer-organisations', b)),
    updateFo: m(({ id, ...b }: { id: string; name?: string; code?: string | null }) =>
      patch<{ id: string }>(`/farmer-organisations/${id}`, b)),
    createFarmer: m((b: { code?: string | null; name?: string | null; supplier_id?: string; fo_id?: string }) =>
      post<Farmer>('/farmers', b)),
    updateFarmer: m(({ id, ...b }: { id: string; code?: string | null; name?: string | null }) =>
      patch<{ id: string }>(`/farmers/${id}`, b)),
    linkFarmer: m(({ id, ...b }: { id: string; supplier_id?: string; fo_id?: string }) =>
      post<{ farmer_id: string }>(`/farmers/${id}/links`, b)),
    unlinkFarmer: m(({ id, ...q }: { id: string; supplier_id?: string | null; fo_id?: string | null }) =>
      del<{ removed: number }>(`/farmers/${id}/links?${searchParams({ supplier_id: q.supplier_id ?? undefined, fo_id: q.fo_id ?? undefined })}`)),
  }
}

export const useCountries = () =>
  useQuery({
    queryKey: keys.countries,
    queryFn: () => get<{ countries: Country[] }>('/countries').then((r) => r.countries),
    staleTime: 5 * 60_000,
  })

export const usePresets = () =>
  useQuery({
    queryKey: keys.presets,
    queryFn: () => get<{ presets: Preset[] }>('/check-presets').then((r) => r.presets),
  })

export function useSavePreset() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string; config: RunConfig }) => post<Preset>('/check-presets', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.presets }),
  })
}

/**
 * Getting plots into a region.
 *
 * There is no dataset to create first: a file lands in the region you name,
 * and what comes back describes the upload — its columns, the guessed
 * mapping — so the mapping step still has something to work on.
 */
export function useUploadToRegion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ regionId, form }: { regionId: string; form: FormData }) =>
      upload<IngestDetail>(`/regions/${regionId}/uploads`, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['regions'] })
      qc.invalidateQueries({ queryKey: ['region'] })
    },
  })
}

export function useSaveDrawnPlots() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ regionId, ...body }: {
      regionId: string
      supplier_id?: string | null
      /** A supplier by name, recorded if new. Ignored when supplier_id is given. */
      supplier_name?: string | null
      country_code?: string | null
      name?: string
      /** The host's own references, given to every plot saved here. */
      refs?: Record<string, string>
      plots: { geometry: GeoJSON.Geometry; properties?: Record<string, unknown> }[]
    }) => post<{ region_id: string; added: number }>(`/regions/${regionId}/drawings`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['regions'] })
      qc.invalidateQueries({ queryKey: ['region'] })
    },
  })
}

/** Corrects the column mapping of one upload, and re-derives every attribute. */
/** Sets the host's own references on an upload and each of its plots. */
export function useUpdateIngestRefs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ingestId, refs }: { ingestId: string; refs: Record<string, string | null> }) =>
      patch<IngestDetail>(`/ingests/${ingestId}/refs`, { refs }),
    onSuccess: (data) => qc.setQueryData(keys.ingest(data.ingest.id), data),
  })
}

export function useUpdateFieldMapping() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ingestId, ...body }: {
      ingestId: string
      mapping: Record<string, string | null>
      country_code?: string
      supplier_id?: string | null
    }) => patch<IngestDetail>(`/ingests/${ingestId}/field-mapping`, body),
    onSuccess: (data) => {
      qc.setQueryData(keys.ingest(data.ingest.id), data)
      qc.invalidateQueries({ queryKey: ['region'] })
    },
  })
}

/** Takes a finished run out of its region's history. */
export function useRemoveRun(regionId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (runId: string) => del<{ id: string }>(`/runs/${runId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['region-runs', regionId] })
      qc.invalidateQueries({ queryKey: ['region', regionId] })
    },
  })
}

/** Takes an uploaded file back out of its region, plots and all. */
export function useRemoveIngest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ingestId: string) =>
      del<{ id: string; plots_deleted: number; plots_kept: number; regions: string[] }>(`/ingests/${ingestId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['regions'] })
      qc.invalidateQueries({ queryKey: ['region'] })
      qc.invalidateQueries({ queryKey: ['region-uploads'] })
      qc.invalidateQueries({ queryKey: ['region-runs'] })
      qc.invalidateQueries({ queryKey: ['run'] })
      qc.invalidateQueries({ queryKey: ['issues'] })
      qc.invalidateQueries({ queryKey: ['plots'] })
      qc.invalidateQueries({ queryKey: ['plot-clusters'] })
    },
  })
}

/** A region's runs, newest first (GET /regions/:id/runs). */
export interface RegionRun {
  id: string
  status: RunStatus
  error: string | null
  created_at: string
  started_at: string | null
  finished_at: string | null
  created_by: string | null
  /** Who started it, for the host's own name (`userLabel`). */
  created_by_id?: string | null
  checks: number
  totals: { plots: number; passed: number; flagged: number; rejected: number; excluded: number; compliant_pct: number } | null
  issues: number | null
}

export const useRegionRuns = (regionId: string | undefined, limit = 25) =>
  useQuery({
    queryKey: ['region-runs', regionId, limit],
    queryFn: () => get<{ runs: RegionRun[]; total: number }>(`/regions/${regionId}/runs?limit=${limit}`),
    enabled: Boolean(regionId),
    placeholderData: (prev) => prev,
    // A run in flight moves on its own.
    refetchInterval: (query) =>
      query.state.data?.runs.some((r) => r.status === 'queued' || r.status === 'running') ? 2000 : false,
  })

/** The files a region's plots came from (GET /regions/:id/uploads). */
export interface RegionUpload {
  id: string
  filename: string | null
  source: string
  feature_count: number
  byte_size: number | null
  created_at: string
  uploaded_by: string | null
  /** Who uploaded it, for the host's own name (`userLabel`). */
  uploaded_by_id?: string | null
  plots_here: number
  plots_elsewhere: number
}

export const useRegionUploads = (regionId: string | undefined) =>
  useQuery({
    queryKey: ['region-uploads', regionId],
    queryFn: () => get<{ uploads: RegionUpload[]; drawn_plots: number }>(`/regions/${regionId}/uploads`),
    enabled: Boolean(regionId),
  })

export function useCreateRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { region_id: string; config?: RunConfig }) =>
      post<{ id: string; status: string }>('/runs', body),
    // The region page reads its latest run from the region, so it must not
    // show the previous one from cache.
    onSuccess: (_run, body) => {
      qc.invalidateQueries({ queryKey: ['regions'] })
      qc.invalidateQueries({ queryKey: keys.region(body.region_id) })
      qc.invalidateQueries({ queryKey: ['region-runs', body.region_id] })
    },
  })
}

/** Polls while the run is in flight, then settles. */
export function useRun(id: string | undefined, options?: Partial<UseQueryOptions<Run>>) {
  return useQuery<Run>({
    queryKey: keys.run(id ?? ''),
    queryFn: () => get<{ run: Run }>(`/runs/${id}`).then((r) => r.run),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'queued' || status === 'running' ? 700 : false
    },
    ...options,
  })
}

export function useIssues(runId: string | undefined, params: Record<string, string | undefined> = {}) {
  const q = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '') as [string, string][],
  ).toString()
  return useQuery({
    queryKey: keys.issues(runId ?? '', q),
    queryFn: () => get<{ issues: Issue[]; total: number }>(`/runs/${runId}/issues${q ? `?${q}` : ''}`),
    enabled: Boolean(runId),
  })
}

export function usePlotsGeoJson(
  runId: string | undefined,
  params: Record<string, string | undefined> = {},
  options: { enabled?: boolean; keepPrevious?: boolean } = {},
) {
  const q = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '') as [string, string][],
  ).toString()
  return useQuery({
    queryKey: keys.plots(runId ?? '', q),
    queryFn: () => get<GeoJSON.FeatureCollection>(`/runs/${runId}/plots.geojson${q ? `?${q}` : ''}`),
    enabled: Boolean(runId) && (options.enabled ?? true),
    // 422 is "too many plots for one response": asking again gets the same.
    retry: (count, err) => !(err instanceof ApiError && err.status === 422) && count < 1,
    // Panning a large region asks for each new area; the outlines already
    // drawn stay until the next ones arrive.
    ...(options.keepPrevious ? { placeholderData: (prev: GeoJSON.FeatureCollection | undefined) => prev } : {}),
  })
}

/** A run's plots grouped by the server for a zoomed-out map (GET /runs/:id/plots/clusters). */
export interface PlotClusterResponse {
  zoom: number
  /** Every plot matching the filters, wherever it is. */
  total: number
  /** [minLng, minLat, maxLng, maxLat] of all of them, to frame the map. */
  extent: [number, number, number, number] | null
  cells: { lng: number; lat: number; count: number; rejected: number; flagged: number; excluded: number }[]
}

export function usePlotClusters(
  runId: string | undefined,
  params: Record<string, string | undefined>,
  options: { enabled?: boolean; keepPrevious?: boolean } = {},
) {
  const q = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '') as [string, string][],
  ).toString()
  return useQuery({
    queryKey: ['plot-clusters', runId ?? '', q],
    queryFn: () => get<PlotClusterResponse>(`/runs/${runId}/plots/clusters?${q}`),
    enabled: Boolean(runId) && (options.enabled ?? true),
    ...(options.keepPrevious ? { placeholderData: (prev: PlotClusterResponse | undefined) => prev } : {}),
  })
}

/** The 1b table. Same filters as the map, so the two always agree. */
export function usePlotRows(runId: string | undefined, params: Record<string, string | undefined> = {}) {
  const q = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '') as [string, string][],
  ).toString()
  return useQuery({
    queryKey: keys.plotRows(runId ?? '', q),
    queryFn: () => get<{ plots: PlotRow[]; total: number }>(`/runs/${runId}/plots${q ? `?${q}` : ''}`),
    enabled: Boolean(runId),
    placeholderData: (prev) => prev,
  })
}

/**
 * One plot in full. `keepPrevious` holds the last plot on screen while the
 * next one loads — the inspect screen steps from plot to plot, and dropping
 * to a skeleton in between rebuilt the whole screen each time.
 */
export const usePlot = (id: string | undefined, runId?: string, options: { keepPrevious?: boolean } = {}) =>
  useQuery({
    queryKey: keys.plot(id ?? '', runId),
    queryFn: () => get<PlotDetail>(`/plots/${id}${runId ? `?run_id=${runId}` : ''}`),
    enabled: Boolean(id),
    ...(options.keepPrevious ? { placeholderData: (previous: PlotDetail | undefined) => previous } : {}),
  })

/**
 * Any mutation that changes a plot refreshes the run, the map and the issue
 * list — and the region's counts and fields table, which otherwise kept the
 * plot as it was before the edit.
 */
function useAfterPlotChange(runId: string | undefined) {
  const qc = useQueryClient()
  return () => {
    if (runId) qc.invalidateQueries({ queryKey: keys.run(runId) })
    qc.invalidateQueries({ queryKey: ['issues'] })
    qc.invalidateQueries({ queryKey: ['plots'] })
    qc.invalidateQueries({ queryKey: ['plot-clusters'] })
    qc.invalidateQueries({ queryKey: ['plot'] })
    qc.invalidateQueries({ queryKey: ['region'] })
    qc.invalidateQueries({ queryKey: ['region-fields'] })
    qc.invalidateQueries({ queryKey: ['region-producers'] })
    // A plot can give its farmer organisation an FO_ID.
    qc.invalidateQueries({ queryKey: ['farmer-organisations'] })
  }
}

export function useRepairPlot(runId: string | undefined) {
  const after = useAfterPlotChange(runId)
  return useMutation({
    mutationFn: ({ plotId, preview }: { plotId: string; preview?: boolean }) =>
      post<{ plot?: Plot; summary?: Run['summary']; preview?: boolean; geometry?: GeoJSON.Geometry; area_ha?: number; delta_ha?: number; ok?: boolean }>(
        `/plots/${plotId}/repairs`,
        { run_id: runId, preview: preview ?? false },
      ),
    onSuccess: (_d, v) => {
      if (!v.preview) after()
    },
  })
}

export function useTrimPlot(runId: string | undefined) {
  const after = useAfterPlotChange(runId)
  return useMutation({
    mutationFn: ({
      plotId, against, relatedPlotId, preview,
    }: { plotId: string; against: 'plot' | 'country'; relatedPlotId?: string; preview?: boolean }) =>
      post<{ plot?: Plot; summary?: Run['summary']; preview?: boolean; geometry?: GeoJSON.Geometry; area_ha?: number; delta_ha?: number; ok?: boolean }>(
        `/plots/${plotId}/trims`,
        { run_id: runId, against, related_plot_id: relatedPlotId, preview: preview ?? false },
      ),
    onSuccess: (_d, v) => {
      if (!v.preview) after()
    },
  })
}

export function useUpdatePlot(runId: string | undefined) {
  const after = useAfterPlotChange(runId)
  return useMutation({
    mutationFn: ({ plotId, ...body }: { plotId: string } & Record<string, unknown>) =>
      patch<{ plot: Plot; summary: Run['summary'] | null }>(`/plots/${plotId}`, { run_id: runId, ...body }),
    onSuccess: after,
  })
}

/**
 * Comments on one plot. The API answers with the plot's whole list, which
 * goes straight into the cached plot, so the panel updates without a refetch.
 */
export function usePlotComments(plotId: string | undefined, runId: string | undefined) {
  const qc = useQueryClient()
  const store = (r: { comments: PlotComment[] }) =>
    qc.setQueryData<PlotDetail>(keys.plot(plotId ?? '', runId), (cur) => (cur ? { ...cur, comments: r.comments } : cur))
  const add = useMutation({
    mutationFn: (body: string) => post<{ comments: PlotComment[] }>(`/plots/${plotId}/comments`, { body }),
    onSuccess: store,
  })
  const remove = useMutation({
    mutationFn: (commentId: string) => del<{ comments: PlotComment[] }>(`/plots/${plotId}/comments/${commentId}`),
    onSuccess: store,
  })
  return { add, remove }
}

export function useExcludePlot(runId: string | undefined) {
  const after = useAfterPlotChange(runId)
  return useMutation({
    mutationFn: ({ plotId, reason, undo }: { plotId: string; reason?: string; undo?: boolean }) =>
      undo
        ? del<{ id: string; status: string }>(`/plots/${plotId}/exclusion${runId ? `?run_id=${runId}` : ''}`)
        : put<{ id: string; status: string }>(`/plots/${plotId}/exclusion`, { run_id: runId, reason }),
    onSuccess: after,
  })
}

export function useRepairAll(runId: string | undefined) {
  const after = useAfterPlotChange(runId)
  return useMutation({
    mutationFn: () =>
      post<{ repaired: number; summary: Run['summary'] | null }>(
        `/runs/${runId}/repairs?check=geometry_errors`,
      ),
    onSuccess: after,
  })
}


/**
 * The drawing screen's live checks: one geometry, no writes.
 *
 * A mutation rather than a query because it is fired by an edit rather than
 * by a cache key, and because two edits in flight should not race to fill one
 * cache entry. The same SQL a real run uses, so a preview and a run agree.
 */
export function usePreviewCheck() {
  return useMutation({
    mutationFn: (body: {
      geometry: GeoJSON.Geometry
      properties?: Record<string, unknown>
      country_code?: string | null
      supplier_id?: string | null
      /** Plots drawn this session but not yet saved, so overlaps are visible. */
      session_plots?: { ref: string | null; geometry: GeoJSON.Geometry }[]
      /** Overlap needs a closed ring; skip it while the shape is being drawn. */
      check_overlaps?: boolean
      config?: unknown
    }) => post<Preview>('/checks/preview', body),
  })
}

/** The next free P-nnn Plot_ID in the organisation, for drawing (AG-84). */
export const useNextPlotId = () =>
  useQuery({
    queryKey: ['next-plot-id'],
    queryFn: () => get<{ plot_id: string; number: number }>('/drawings/next-plot-id'),
    staleTime: 0,
  })

export const useRegions = () =>
  useQuery({
    queryKey: keys.regions,
    queryFn: () => get<{ regions: RegionSummary[] }>('/regions').then((r) => r.regions),
  })

export const useRegion = (id: string | undefined) =>
  useQuery({
    queryKey: keys.region(id ?? ''),
    queryFn: () => get<RegionDetail>(`/regions/${id}`),
    enabled: Boolean(id),
  })

/** Builds the query string the way usePlotRows does, dropping empty values. */
export const query = (params: Record<string, string | undefined>) =>
  new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '') as [string, string][],
  ).toString()

export function useRegionFields(
  id: string | undefined, params: Record<string, string | undefined> = {},
) {
  const q = query(params)
  return useQuery({
    queryKey: keys.regionFields(id ?? '', q),
    queryFn: () =>
      get<{ fields: RegionField[]; total: number; benchmark: Benchmark }>(
        `/regions/${id}/fields${q ? `?${q}` : ''}`,
      ),
    enabled: Boolean(id),
    // Keeps the table from flashing empty while the threshold is being typed.
    placeholderData: (prev) => prev,
  })
}

export function useRegionProducers(
  id: string | undefined, params: Record<string, string | undefined> = {},
) {
  const q = query(params)
  return useQuery({
    queryKey: keys.regionProducers(id ?? '', q),
    queryFn: () =>
      get<{
        producers: Producer[]; totals: ProducerTotals; benchmark: Benchmark
        group: string; threshold: number; share_threshold: number; season: number | null
      }>(`/regions/${id}/producers${q ? `?${q}` : ''}`),
    enabled: Boolean(id),
    placeholderData: (prev) => prev,
  })
}

/** Everything that changes a region invalidates the same three caches. */
function useAfterRegionChange() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ['regions'] })
    qc.invalidateQueries({ queryKey: ['region'] })
    qc.invalidateQueries({ queryKey: ['region-fields'] })
    qc.invalidateQueries({ queryKey: ['region-producers'] })
  }
}

export function useCreateRegion() {
  const after = useAfterRegionChange()
  return useMutation({
    mutationFn: (body: { name: string; description?: string; country_code?: string }) =>
      post<Region>('/regions', body),
    onSuccess: after,
  })
}

/** Renames a region or sets its country. */
export function useUpdateRegion() {
  const after = useAfterRegionChange()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; country_code?: string | null }) =>
      patch<Region>(`/regions/${id}`, body),
    onSuccess: after,
  })
}

/**
 * Moves named plots into a region.
 *
 * The region is part of the payload rather than bound at hook construction:
 * a caller that picks the region from a list would otherwise fire the first
 * click against the previous render's value.
 */
export function useAddPlotsToRegion() {
  const after = useAfterRegionChange()
  return useMutation({
    mutationFn: ({ regionId, ...body }: {
      regionId: string
      plot_ids?: string[]
    }) => post<{ added: number; plot_count: number }>(`/regions/${regionId}/plots`, body),
    onSuccess: after,
  })
}

export function useImportRegionYields(id: string | undefined) {
  const after = useAfterRegionChange()
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return upload<{ rows: number; matched: number; unmatched: string[] }>(
        `/regions/${id}/yields`, form,
      )
    },
    onSuccess: after,
  })
}

/** Which basemaps to draw, and from where. Constant for a deployment. */
/** Esri's, which is what the defaults below describe. */
const BASEMAP_DEFAULTS: Record<BasemapStyle['id'], { attribution: string; maxzoom: number }> = {
  light: { attribution: 'Esri, HERE, Garmin, © OpenStreetMap contributors', maxzoom: 16 },
  labels: { attribution: 'Esri, HERE, Garmin, © OpenStreetMap contributors', maxzoom: 16 },
  satellite: { attribution: 'Esri, Maxar, Earthstar Geographics', maxzoom: 19 },
}

/**
 * The map's three basemap styles.
 *
 * Each one configured on the client (`basemaps`) is fetched by the browser
 * from its provider, so its tiles never reach the API — every proxied tile is
 * a call to the API, and a map view asks for dozens at once. Any not
 * configured come from `GET /basemap`, through the proxy as before; with
 * all three configured the API is not asked at all.
 */
export const useBasemapStyles = () =>
  useQuery({
    queryKey: keys.basemap,
    queryFn: async (): Promise<BasemapStyle[]> => {
      const configured = getConfig().basemaps ?? {}
      const ids: BasemapStyle['id'][] = ['light', 'labels', 'satellite']
      const own = new Map(ids.flatMap((id) => {
        const given = configured[id]
        if (!given) return []
        const { url, attribution, maxzoom } = typeof given === 'string' ? { url: given } : given
        return [[id, {
          id, tiles: [url], tileSize: 256,
          maxzoom: maxzoom ?? BASEMAP_DEFAULTS[id].maxzoom,
          attribution: attribution ?? BASEMAP_DEFAULTS[id].attribution,
        } satisfies BasemapStyle] as const]
      }))
      if (own.size === ids.length) return ids.map((id) => own.get(id)!)
      const proxied = await get<{ styles: BasemapStyle[] }>('/basemap').then((r) => r.styles)
      return proxied.map((style) => own.get(style.id) ?? style)
    },
    staleTime: Infinity,
  })
